import * as Sentry from "@sentry/react";

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  if (!dsn) {
    // Silently skip Sentry initialization if DSN not configured
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.PROD ? "production" : "development",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Configure which URLs to trace
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/.*\.lovableproject\.com/,
      /^https:\/\/.*\.supabase\.co/
    ],

    // Ignore errors from browser extensions and third-party scripts
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      /^safari-web-extension:\/\//i,
      /^webkit-masked-url:\/\//i,
      /googletagmanager\.com/i,
      /google-analytics\.com/i,
    ],

    beforeSend(event) {
      // Filter out errors from browser extensions
      if (event.exception) {
        const error = event.exception.values?.[0];
        if (error?.stacktrace?.frames) {
          const frames = error.stacktrace.frames;
          if (frames.some(frame => 
            frame.filename?.includes('extension://') ||
            frame.filename?.includes('moz-extension://') ||
            frame.filename?.includes('safari-extension://')
          )) {
            return null;
          }
        }
      }

      // Don't send events in development unless explicitly enabled
      if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_DEV) {
        return null;
      }

      // PII scrubbing
      if (event.user) {
        if ('email' in event.user) delete (event.user as any).email;
        if ('username' in event.user) delete (event.user as any).username;
        if ('ip_address' in event.user) delete (event.user as any).ip_address;
      }
      if (event.request?.headers) {
        const headers = event.request.headers as Record<string, string>;
        if (headers['Authorization']) headers['Authorization'] = 'REDACTED';
        if (headers['authorization']) headers['authorization'] = 'REDACTED';
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map(b => {
          if (b.data && typeof b.data === 'object') {
            const data = { ...b.data } as any;
            if (data.url) data.url = String(data.url).split('?')[0];
            if (data.email) data.email = 'redacted';
            return { ...b, data };
          }
          return b;
        });
      }

      return event;
    },

    // Tag releases with version
    release: import.meta.env.VITE_APP_VERSION || "unknown",
  });
}

// Utility functions for manual error tracking
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    tags: {
      component: context?.component || "unknown",
      action: context?.action || "unknown",
    },
    ...(context && { extra: context }),
  });
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = "info", context?: Record<string, any>) {
  Sentry.captureMessage(message, {
    level,
    tags: {
      component: context?.component || "unknown",
      action: context?.action || "unknown",
    },
    ...(context && { extra: context }),
  });
}

export function setUserContext(user: { id: string; email?: string; role?: string }) {
  Sentry.setUser({
    id: user.id,
    ...(user.email && { email: user.email, username: user.email }),
    ...(user.role && { role: user.role }),
  });
}

export function addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    category,
    level: "info",
    ...(data && { data }),
  });
}

// Performance monitoring utilities
export function measurePerformance<T>(
  name: string,
  operation: string,
  fn: () => T | Promise<T>
): T | Promise<T> {
  return Sentry.startSpan(
    {
      name,
      op: operation,
    },
    fn
  );
}

// React component error boundaries with Sentry
export const SentryErrorBoundary = Sentry.withErrorBoundary;

export { Sentry };